import { Dispatch, SetStateAction, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  DialogTitle,
} from '@mui/material';
import {
  GridRowId,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@src/constants/queryKeys';
import useWidgetConfigMutations from '@src/hooks/api/mutations/useWidgetConfigMutations';
import JSONEditor from '@src/components/_elements/JSONEditor/JSONEditor';
import { CollectionPayload } from '@src/types/Collection';
import {
  WidgetConfigCreateOrEdit,
  WidgetConfigExtended,
  WidgetCreate,
} from '@src/types/Widget';
import widgetConfigSx from '../configs/widgetConfigSx';
import useAlias from '@alias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';

interface DialogProps {
  jsonData: WidgetConfigExtended;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>;
  handleCancel: (id?: GridRowId) => () => void;
  setJsonPayload: Dispatch<SetStateAction<CollectionPayload | null>>;
}

function WidgetConfigDialog({
  jsonData,
  handleCancel,
  setOpen,
  setRowModesModel,
  setJsonPayload,
}: DialogProps) {
  const alias = useAlias();
  const {
    id,
    remove,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    isNew: _isNew,
    ...rest
  } = jsonData;
  const [newData, setNewData] = useState<
    WidgetCreate | WidgetConfigCreateOrEdit
  >(rest);
  const queryClient = useQueryClient();

  const getTitle = () => {
    if (remove) {
      return alias('Are you sure you want to delete this config?');
    }
    if (id) {
      return alias('Edit widget config');
    }

    return alias('Add widget config');
  };

  const { createWidgetConfig, updateWidgetConfig, deleteWidgetConfig } =
    useWidgetConfigMutations(id);

  const handleSuccessCallback = () => {
    if (id && !remove) {
      setRowModesModel((prevState) => ({
        ...prevState,
        [id]: { mode: GridRowModes.View },
      }));
    }

    queryClient.invalidateQueries({
      queryKey: [QueryKeys.widgets],
    });
    setJsonPayload(null);
    setOpen(false);
  };

  const handleCreate = () =>
    createWidgetConfig.mutate(newData, {
      onSuccess: handleSuccessCallback,
    });

  const handlePCUpdate = () =>
    updateWidgetConfig.mutate(newData, {
      onSuccess: handleSuccessCallback,
    });

  const handleDelete = () =>
    deleteWidgetConfig.mutate(newData, {
      onSuccess: handleSuccessCallback,
    });

  const handleConfirm = () => {
    if (remove) {
      return handleDelete();
    }
    if (id) {
      return handlePCUpdate();
    }
    return handleCreate();
  };

  return (
    <Dialog
      open
      PaperProps={
        !remove
          ? {
              sx: widgetConfigSx.pcDialogPaper,
            }
          : {}
      }
    >
      <DialogTitle>{getTitle()}</DialogTitle>
      {!remove && (
        <DialogContent>
          <JSONEditor
            json={newData}
            onChange={setNewData}
            options={{ mode: 'code' }}
          />
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleCancel(id)}>
          <FormattedAlias id='Cancel' />
        </Button>
        <Button size='small' variant='contained' onClick={handleConfirm}>
          <FormattedAlias id='Confirm' />
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WidgetConfigDialog;
